import { useEffect, useRef } from 'react';

// How much of the next thumb to keep visible, as a fraction of a thumb's size,
// so the strip doesn't look like it ends at the selected tile
const NEIGHBOUR_PEEK = 0.6;
const prefersReducedMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Keeps the selected thumbnail in view. Not scrollIntoView(), which centres on
// every change; here the strip only moves when the selection nears an edge.
const useScrollSelectedIntoView = ({
  axis = 'x',
  scrollerRef,
  selectedIndex
}) => {
  const frameRef = useRef(null);
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || selectedIndex == null || selectedIndex < 0) {
      return undefined;
    }

    // Coalesce a burst of selection changes into a single scroll, aimed at
    // wherever the selection has landed by the time the frame runs
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
    }
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      const isHorizontal = axis === 'x';
      const selected = scroller.children[selectedIndex];
      if (!selected) {
        return;
      }
      const scrollerRect = scroller.getBoundingClientRect();
      const selectedRect = selected.getBoundingClientRect();
      const start = isHorizontal ? selectedRect.left - scrollerRect.left : selectedRect.top - scrollerRect.top;
      const size = isHorizontal ? selectedRect.width : selectedRect.height;
      const viewport = isHorizontal ? scroller.clientWidth : scroller.clientHeight;
      const current = isHorizontal ? scroller.scrollLeft : scroller.scrollTop;
      const peek = size * NEIGHBOUR_PEEK;
      const end = start + size;

      // Already comfortably inside, including the peek margin: leave it be
      let delta = 0;
      if (start < peek) {
        delta = start - peek;
      } else if (end > viewport - peek) {
        delta = end - (viewport - peek);
      }
      if (!delta) {
        return;
      }
      const max = isHorizontal ? scroller.scrollWidth - scroller.clientWidth : scroller.scrollHeight - scroller.clientHeight;
      const target = Math.max(0, Math.min(current + delta, max));
      if (Math.abs(target - current) < 1) {
        return;
      }
      scroller.scrollTo({
        [isHorizontal ? 'left' : 'top']: target,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
    });
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [axis, scrollerRef, selectedIndex]);
};
export default useScrollSelectedIntoView;